// ConsumeScreen.jsx (drop-in replacement)
// - Summary with goal colors (toggleable)
// - Per-item macro/micro + eatenOn time pill
// - NEW: delete item (trash icon + confirm)
// - NEW: edit time by clicking the time pill (date preserved)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { consumeApi } from '../../../api/consumeApi.js';
import { ConsumeQuickPickModal } from './consumeQuickPick.js';
import { SandwichModal } from './sandwichModal.js';
import { AppLayout } from '../../app/appLayout.js';
import './viewHealthScreen.css';

const MEAL_BUTTONS = [
  { label: 'Breakfast', color: '#FB8C00', text: '#FFFFFF' },
  { label: 'Lunch',     color: '#FBC02D', text: '#111111' },
  { label: 'Dinner',    color: '#1E88E5', text: '#FFFFFF' },
  { label: 'Snack',     color: '#43A047', text: '#FFFFFF' },
  { label: 'Sandwich',  color: '#8E24AA', text: '#FFFFFF' },
];

const PRIMARY_NUTRIENTS = [
  { key: 'calories',      label: 'Calories', unit: 'kcal' },
  { key: 'totalCarbG',    label: 'Carbs',    unit: 'g'    },
  { key: 'proteinG',      label: 'Protein',  unit: 'g'    },
  { key: 'totalFatG',     label: 'Fat',      unit: 'g'    },
];

const SECONDARY_PILLS = [
  { key: 'saturatedFatG', label: 'Saturated Fat',  unit: 'g'  },
  { key: 'fiberG',        label: 'Fiber',          unit: 'g'  },
  { key: 'sodiumMg',      label: 'Sodium',         unit: 'mg' },
  { key: 'cholesterolMg', label: 'Chol',           unit: 'mg' },
  { key: 'caffeineMg',    label: 'Caffeine',       unit: 'mg' },
  { key: 'alcoholG',      label: 'Alcohol',        unit: 'g'  },
  { key: 'potassium',     label: 'Potassium',      unit: 'mg' },
  { key: 'calcium',       label: 'Calcium',        unit: 'mg' },
  { key: 'iron',          label: 'Iron',           unit: 'mg' },
];

const MICRO_NUTRIENTS = [
  { key: 'vitaminC', label: 'Vitamin C', unit: 'mg' },
  { key: 'sodiumMg', label: 'Sodium', unit: 'mg' },
  { key: 'vitaminD', label: 'Vitamin D', unit: 'mg' },
  { key: 'calcium',  label: 'Calcium',   unit: 'mg' },
  { key: 'iron',     label: 'Iron',      unit: 'mg' },
  { key: 'potassium',label: 'Potassium', unit: 'mg'},
];

// === Goals config & status helpers ===============================
const GOAL_MODE = { limit: 'limit', target: 'target' };

const NUTRIENT_GOAL_MODE = {
  calories: GOAL_MODE.limit,
  totalFatG: GOAL_MODE.limit,
  totalCarbG: GOAL_MODE.target,
  proteinG: GOAL_MODE.target,

  totalSugarG: GOAL_MODE.limit,
  saturatedFatG: GOAL_MODE.limit,
  fiberG: GOAL_MODE.target,
  sodiumMg: GOAL_MODE.limit,
  cholesterolMg: GOAL_MODE.limit,
  caffeineMg: GOAL_MODE.limit,
  alcoholG: GOAL_MODE.limit,
  potassium: GOAL_MODE.target,
  calcium: GOAL_MODE.target,
  iron: GOAL_MODE.target,
};

const LIMIT_WARN_MAX = 1.25;  // 100–125% = orange
const TARGET_BAD_MAX = 0.60;  // <60% = red
const TARGET_WARN_MAX = 0.90; // 60–90% = orange

function classifyStatus({ value, recommended, mode }) {
  // Special case: zero-limit goals (e.g., alcohol, trans fat if added)
  if (mode === GOAL_MODE.limit && Number(recommended) === 0) {
    if (value == null) return 'neutral';
    return Number(value) > 0 ? 'bad' : 'good';
  }

  if (recommended == null || recommended <= 0 || value == null) return 'neutral';
  const ratio = Number(value) / Number(recommended);
  if (!Number.isFinite(ratio)) return 'neutral';

  if (mode === GOAL_MODE.limit) {
    if (ratio <= 1) return 'good';
    if (ratio <= LIMIT_WARN_MAX) return 'warn';
    return 'bad';
  }
  if (ratio < TARGET_BAD_MAX) return 'bad';
  if (ratio < TARGET_WARN_MAX) return 'warn';
  return 'good';
}

function pillClass(status, show) {
  if (!show) return '';
  if (status === 'good') return 'is-good';
  if (status === 'warn') return 'is-warn';
  if (status === 'bad')  return 'is-bad';
  return '';
}

const COLORIZE_PRIMARY = new Set(['calories', 'totalFatG', 'proteinG', 'totalCarbG']);
// =================================================================

function formatNumber(n) {
  if (n == null) return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  if (num % 1 === 0) return String(num);
  if (num < 1) return num.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
  return num.toFixed(1).replace(/0+$/, '').replace(/\.$/, '');
}

function getTooltipText(key, recommended, mode) {
  if (recommended == null || recommended <= 0) return null;
  const modeText = mode === GOAL_MODE.limit ? 'Limit' : 'Target';
  return `${modeText}: ${formatNumber(recommended)}`;
}

function multiply(n, factor) {
  if (n == null) return 0;
  const v = Number(n) * Number(factor || 0);
  return Math.round(v * 10) / 10;
}

// Format "7:34 AM" (label) and full local datetime (title)
function formatEatenOn(eatenOn) {
  if (!eatenOn) return null;
  const d = new Date(eatenOn);
  if (isNaN(d.getTime())) return null;
  const label = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  const title = d.toLocaleString();
  return { d, label, title };
}

// For <input type="time"> value (24h "HH:MM")
function timeForInput(dateLike) {
  const d = new Date(dateLike);
  if (isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Preserve date; replace time with HH:MM (local), return ISO string
function replaceTimeKeepDate(dateLike, hhmm) {
  const d = new Date(dateLike);
  if (isNaN(d.getTime())) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const updated = new Date(d);
  updated.setHours(Number(h) || 0, Number(m) || 0, 0, 0);
  return updated.toISOString();
}

// Prefer entry.id, fallback to itemModel.id
function getEntryId(entry) {
  return entry?.id ?? entry?.itemModel?.id;
}

const ConsumeScreen = () => {
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [summary, setSummary] = useState(null);
  const [recommended, setRecommended] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalMeal, setModalMeal] = useState(null);
  const [showSandwichModal, setShowSandwichModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [error, setError] = useState(null);
  const [showColors, setShowColors] = useState(false);

  const MEAL_FETCHERS = {
    Breakfast: () => consumeApi.recommendBreakfast(),
    Lunch:     () => consumeApi.recommendLunch(),
    Dinner:    () => consumeApi.recommendDinner(),
    Snack:     () => consumeApi.recommendSnack(),
  };

  useEffect(() => {
    let isCancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const dateStr = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/New_York',
        }).format(selectedDate);

        const resp = await consumeApi.getConsumptionInfo(dateStr);

        if (!isCancelled) {
          const rows = resp?.data?.rows;
          if (rows?.summary && Array.isArray(rows?.items)) {
            setSummary(rows.summary);
            setRecommended(rows.recommended || null);
            setItems(rows.items);
          } else if (Array.isArray(rows)) {
            setSummary(null);
            setRecommended(null);
            setItems(rows.map((r) => ({
              id: r.id, // keep if present
              itemModel: { name: r.item || r.name || 'Item', calories: r.calories },
              recipeName: r.item || r.name,
              servingsEaten: r.qty || 1,
              eatenOn: r.eatenOn,
            })));
          } else {
            setSummary(null);
            setRecommended(null);
            setItems([]);
          }
        }
      } catch (e) {
        if (!isCancelled) {
          console.error('Error fetching health data:', e);
          setError('Failed to load data');
          setSummary(null);
          setRecommended(null);
          setItems([]);
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { isCancelled = true; };
  }, [selectedDate, refreshTrigger]);

  const goPrevDay = useCallback(() => {
    setSelectedDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1));
  }, []);
  const goNextDay = useCallback(() => {
    setSelectedDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1));
  }, []);
  const openMealModal = useCallback((meal) => {
    if (meal === 'Sandwich') {
      setShowSandwichModal(true);
    } else {
      setModalMeal(meal);
    }
  }, []);
  const closeMealModal = useCallback(() => { setModalMeal(null); }, []);
  const closeSandwichModal = useCallback(() => { setShowSandwichModal(false); }, []);

  const dayTitle = useMemo(() => selectedDate.toLocaleDateString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  }), [selectedDate]);

  const getStatus = useCallback((key, value) => {
    const rec = recommended?.[key];
    const mode = NUTRIENT_GOAL_MODE[key] || GOAL_MODE.limit;
    return classifyStatus({ value, recommended: rec, mode });
  }, [recommended]);

  // === Item actions =================================================

  // Delete with confirm, then update local state
  const handleDelete = useCallback(async (entry) => {
    const id = getEntryId(entry);
    if (!id) return;
    const name = entry?.recipeName || entry?.itemModel?.name || 'this item';
    const ok = window.confirm(`Remove ${name}?`);
    if (!ok) return;
    try {
      await consumeApi.removeConsumedItem(id);
      setItems((prev) => prev.filter((x) => getEntryId(x) !== id));
    } catch (e) {
      console.error('Failed to remove item', e);
      alert('Sorry, failed to remove that item.');
    }
  }, []);

  // Edit time only; keep same calendar date from eatenOn (or selectedDate if missing)
  const handleEditTime = useCallback(async (entry, hhmm) => {
    const id = getEntryId(entry);
    if (!id) return;

    // Base date = eatenOn if present else selectedDate
    const base = entry?.eatenOn ? new Date(entry.eatenOn) : new Date(selectedDate);
    const iso = replaceTimeKeepDate(base, hhmm);
    if (!iso) return;

    try {
      await consumeApi.editConsumedTime(id, { eatenOn: iso });
      // Update local state
      setItems((prev) =>
        prev.map((x) =>
          getEntryId(x) === id ? { ...x, eatenOn: iso } : x
        )
      );
    } catch (e) {
      console.error('Failed to update time', e);
      alert('Sorry, failed to update the time.');
    }
  }, [selectedDate]);

  // ================================================================

  return (
    <AppLayout>
      <div className="wr-cal-container">
        <div className="wr-header-row">
          <h2 className="wr-title">Consume</h2>

          {/* Meal buttons */}
          <div className="wr-meal-buttons">
            {MEAL_BUTTONS.map((btn) => (
              <button
                key={btn.label}
                onClick={() => openMealModal(btn.label)}
                style={{ background: btn.color, color: btn.text }}
                className="wr-meal-btn"
              >
                {btn.label}
              </button>
            ))}
          </div>

          {/* Toggle for colors */}
          <label className="wr-toggle">
            <input
              type="checkbox"
              checked={showColors}
              onChange={(e) => setShowColors(e.target.checked)}
            />
            <span>Show goal colors</span>
          </label>
        </div>

        <div className="wr-mobile-day">
          <div className="wr-mobile-day-header">
            <button className="wr-nav-btn" onClick={goPrevDay} aria-label="Previous day">‹</button>
            <div className="wr-mobile-day-title">{dayTitle}</div>
            <button className="wr-nav-btn" onClick={goNextDay} aria-label="Next day">›</button>
          </div>

          {/* SUMMARY */}
          <section className="wr-summary-section">
            {loading && <div className="wr-loading">Loading…</div>}
            {error && <div className="wr-error">{error}</div>}

            {!loading && !error && (
              <>
                {/* Primary cards */}
                <div className="wr-summary-grid">
                  {PRIMARY_NUTRIENTS.map((n) => {
                    const val = summary?.[n.key] ?? 0;
                    const status = getStatus(n.key, val);
                    const colorClass = COLORIZE_PRIMARY.has(n.key) ? pillClass(status, showColors) : '';
                    const mode = NUTRIENT_GOAL_MODE[n.key] || GOAL_MODE.limit;
                    const tooltip = getTooltipText(n.key, recommended?.[n.key], mode);
                    return (
                      <article 
                        className={`wr-card wr-card-primary ${colorClass}`} 
                        key={n.key}
                        title={tooltip || undefined}
                      >
                        <div className="wr-card-kicker">{n.label}</div>
                        <div className="wr-card-metric">
                          {formatNumber(val)}
                          <span className="wr-card-unit">{n.unit}</span>
                        </div>
                      </article>
                    );
                  })}
                </div>

                {/* Secondary pills */}
                <div className="wr-pill-row">
                  {SECONDARY_PILLS.map((n) => {
                    const val = summary?.[n.key] ?? 0;
                    const status = getStatus(n.key, val);
                    const mode = NUTRIENT_GOAL_MODE[n.key] || GOAL_MODE.limit;
                    const tooltip = getTooltipText(n.key, recommended?.[n.key], mode);
                    return (
                      <span 
                        className={`wr-pill ${pillClass(status, showColors)}`} 
                        key={n.key}
                        title={tooltip || undefined}
                      >
                        {n.label}: {formatNumber(val)}{n.unit}
                      </span>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          {/* ITEMS */}
          <section className="wr-items-section">
            {!loading && !error && items?.length === 0 && (
              <div className="wr-empty">No entries for this date.</div>
            )}

            {!loading && !error && items?.length > 0 && (
              <ul className="wr-item-list">
                {items.map((it, idx) => (
                  <li className="wr-item" key={getEntryId(it) ?? idx}>
                    <ItemCard
                      entry={it}
                      onDelete={() => handleDelete(it)}
                      onEditTime={(hhmm) => handleEditTime(it, hhmm)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </div>

      {/* Quick Pick Modal */}
      {modalMeal && MEAL_FETCHERS[modalMeal] && (
        <ConsumeQuickPickModal
          title={`Eat ${modalMeal}`}
          fetcher={MEAL_FETCHERS[modalMeal]}
          onClose={closeMealModal}
        />
      )}

      {/* Sandwich builder modal */}
      {showSandwichModal && (
        <SandwichModal
          onClose={closeSandwichModal}
          onSuccess={() => setRefreshTrigger((t) => t + 1)}
        />
      )}
    </AppLayout>
  );
};

function ItemCard({ entry, onDelete, onEditTime }) {
  const [open, setOpen] = useState(false);
  const [editingTime, setEditingTime] = useState(false);
  const m = entry?.itemModel || {};
  const servings = entry?.servingsEaten || 1;
  const displayName = entry?.recipeName || m?.name || 'Item';
  let subName = m?.brand;

  if (entry?.recipeName !== null) {
    subName = m.name;
  }

  const eaten = formatEatenOn(entry?.eatenOn);

  const [timeValue, setTimeValue] = useState(() => (eaten ? timeForInput(eaten.d) : ''));

  // Keep input value in sync if eatenOn changes
  useEffect(() => {
    setTimeValue(eaten ? timeForInput(eaten.d) : '');
  }, [entry?.eatenOn]);

  const macros = [
    { key: 'calories',   label: 'Cal',   unit: 'kcal' },
    { key: 'totalCarbG', label: 'Carbs', unit: 'g'    },
    { key: 'proteinG',   label: 'Protein', unit: 'g'  },
    { key: 'totalFatG',  label: 'Fat',   unit: 'g'    },
    { key: 'saturatedFatG',label: 'Saturated Fat', unit: 'g'    },
    { key: 'fiberG',     label: 'Fiber', unit: 'g'    },
  ];

  const startEdit = () => {
    if (!eaten) return;
    setEditingTime(true);
  };

  const cancelEdit = () => {
    setTimeValue(eaten ? timeForInput(eaten.d) : '');
    setEditingTime(false);
  };

  const saveEdit = async () => {
    if (!timeValue) return cancelEdit();
    await onEditTime(timeValue);
    setEditingTime(false);
  };

  return (
    <article className={`wr-card wr-item-card ${open ? 'is-open' : ''}`}>
      <header className="wr-item-header">
        <div className="wr-item-title">
          <div className="wr-item-name">{displayName}</div>
          {subName && <div className="wr-item-brand">{subName}</div>}
        </div>

        {/* Right-side meta: time/edit + servings + delete */}
        <div className="wr-item-meta">
          {editingTime ? (
            <div className="wr-time-edit">
              <input
                type="time"
                value={timeValue}
                onChange={(e) => setTimeValue(e.target.value)}
              />
              <button className="wr-btn wr-btn-small" onClick={saveEdit} aria-label="Save time">Save</button>
              <button className="wr-btn wr-btn-small wr-btn-ghost" onClick={cancelEdit} aria-label="Cancel">Cancel</button>
            </div>
          ) : eaten ? (
            <button
              className="wr-time-pill wr-time-pill--button"
              title={`${eaten.title} — click to edit time`}
              onClick={startEdit}
            >
              {eaten.label}
            </button>
          ) : null}

          <div className="wr-item-servings">
            <span className="wr-muted">Servings</span>
            <strong>{formatNumber(servings)}</strong>
          </div>

          {/* Trash icon button */}
          <button
            className="wr-icon-btn wr-trash-btn"
            onClick={onDelete}
            aria-label="Delete item"
            title="Delete item"
          >
            {/* inline SVG trash can to avoid extra deps */}
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M9 3h6a1 1 0 0 1 1 1v1h4v2H4V5h4V4a1 1 0 0 1 1-1zm1 4h4v12a2 2 0 0 1-2 2h0a2 2 0 0 1-2-2V7z" fill="currentColor"/>
            </svg>
          </button>
        </div>
      </header>

      <div className="wr-macro-row">
        {macros.map((n) => (
          <div className="wr-macro" key={n.key}>
            <div className="wr-macro-label">{n.label}</div>
            <div className="wr-macro-value">
              {formatNumber(multiply(m?.[n.key], servings))}
              <span className="wr-macro-unit">{n.unit}</span>
            </div>
          </div>
        ))}
      </div>

      <button className="wr-item-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? 'Hide nutrition' : 'More nutrition'}
      </button>

      {open && (
        <div className="wr-micro-grid">
          {MICRO_NUTRIENTS.map((n) => (
            <div className="wr-micro" key={n.key}>
              <div className="wr-micro-label">{n.label}</div>
              <div className="wr-micro-value">
                {formatNumber(multiply(m?.[n.key], servings))}{n.unit}
              </div>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

export { ConsumeScreen };
